import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  Shield, 
  Clock, 
  CheckCircle2, 
  Menu, 
  X, 
  ArrowRight, 
  Users, 
  BrainCircuit,
  Lock,
  Mail,
  MapPin,
  Building2,
  Send,
  FileText,
  AlertTriangle
} from 'lucide-react';

// Reusable Logo Component
const BrandLogo = ({ className = "" }) => (
  <div className={`relative flex items-center justify-center overflow-hidden rounded-xl ${className}`}>
    <img 
      src="JPQuant_Logo1.png" 
      alt="Our Wealth Era Bull Logo" 
      className="w-full h-full object-cover"
    />
  </div>
);

// New Reusable Slogan Component
const Slogan = ({ className = "", topSize = "text-xl md:text-2xl", bottomSize = "text-lg md:text-xl", align = "items-center text-center" }) => (
  <div className={`flex flex-col ${align} ${className}`}>
    <span className={`font-bold text-white tracking-wide ${topSize}`}>
      Unlocking global potential.
    </span>
    <span className={`italic text-amber-400 font-light ${bottomSize}`}>
      The new era of automated wealth.
    </span>
  </div>
);

const OurWealthEraWebsite = () => {
  const [activeTab, setActiveTab] = useState('course');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTosOpen, setIsTosOpen] = useState(false);

  const navItems = [
    { id: 'about', label: 'About Us' },
    { id: 'course', label: 'Passive Investing Course' },
    { id: 'purchase', label: 'Purchase Access' },
    { id: 'application', label: 'Apply for Access' },
  ];

  const handleNavClick = (id) => {
    setActiveTab(id);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-amber-500/30">
      {/* Navigation Bar */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center cursor-pointer group" onClick={() => handleNavClick('course')}>
              <BrandLogo className="w-12 h-12 mr-4 transition-transform group-hover:scale-105" />
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">OUR WEALTH | ERA</h1>
                <p className="text-xs text-amber-500 font-medium tracking-widest uppercase">Methodology</p>
              </div>
            </div>
            <div className="hidden md:flex space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    activeTab === item.id 
                      ? 'text-amber-400 border-b-2 border-amber-400 pb-1' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="md:hidden">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-slate-300 hover:text-white">
                {isMobileMenuOpen ? <X /> : <Menu />}
              </button>
            </div>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`block w-full text-left px-3 py-4 text-base font-medium ${
                    activeTab === item.id 
                      ? 'text-amber-400 bg-slate-800' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content Area */}
      <main className="pt-20">
        <AnimatePresence mode="wait">
          {activeTab === 'course' && <CoursePage key="course" onCtaClick={() => handleNavClick('purchase')} />}
          {activeTab === 'about' && <AboutPage key="about" />}
          {activeTab === 'purchase' && <PurchasePage key="purchase" />}
          {activeTab === 'application' && <ApplicationPage key="application" />}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row justify-between items-start gap-8">
          <div className="flex items-center gap-4">
            <BrandLogo className="w-16 h-16" />
            <div>
              <h2 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-amber-500 mb-1">OUR WEALTH | ERA</h2>
              {/* Slogan integrated into footer */}
              <Slogan 
                align="items-start text-left" 
                topSize="text-sm" 
                bottomSize="text-xs" 
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center h-full sm:pt-4">
            <button
              onClick={() => setIsTosOpen(true)}
              className="flex items-center text-slate-400 hover:text-amber-400 text-sm font-medium transition-colors duration-200 group"
            >
              <FileText className="w-4 h-4 mr-2 group-hover:text-amber-400" />
              Terms of Service
            </button>
            <span className="hidden sm:block text-slate-700">|</span>
            <p className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} Our Wealth | Era Method. All rights reserved.
            </p>
          </div>
        </div>

        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-500/70 shrink-0 mt-0.5" />
              <p className="text-amber-500/70 text-xs font-bold uppercase tracking-widest">Important Disclosures</p>
            </div>
            <div className="space-y-3 text-slate-600 text-xs leading-relaxed">
              <p>
                Our Wealth | Era is an independent financial education company. We are not a licensed broker, registered investment advisor, or financial institution, and we do not provide personalised investment advice or execute trades on behalf of any individual. The <strong className="text-slate-500">Our Wealth | Era JPBot</strong> is a client-operated execution tool — its deployment, configuration, and use remain entirely in the hands of the user at all times.
              </p>
              <p className="text-slate-500">
                <strong className="text-slate-400">Risk Disclosure:</strong> Trading futures and forex instruments involves a significant degree of financial risk and may result in the partial or total loss of your invested capital — and in certain circumstances, losses may exceed your initial deposit. Only capital you can genuinely afford to lose should ever be allocated to speculative trading activity. Any performance figures referenced within our course materials, live sessions, or community content reflect past results and are not a reliable indicator of future outcomes. All trading decisions and their consequences remain solely the responsibility of the individual trader.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms of Service Modal */}
      <AnimatePresence>
        {isTosOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm"
            onClick={() => setIsTosOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.25 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-screen overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-8 py-5 flex items-center justify-between rounded-t-2xl z-10">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-lg">Terms of Service</h2>
                    <p className="text-slate-500 text-xs">Our Wealth | Era Method</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsTosOpen(false)}
                  className="text-slate-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="px-8 py-6 space-y-6 text-sm text-slate-400 leading-relaxed">
                <p className="text-slate-300 text-base">
                  By accessing the Our Wealth | Era educational platform, proprietary software tools, community, or any associated content, you acknowledge and agree to the following terms in full.
                </p>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">1. Educational Use Only</h3>
                  <p>
                    All course content, methodologies, frameworks, and materials produced and delivered by Our Wealth | Era are intended exclusively for educational and informational purposes. Nothing contained within our program constitutes financial advice, personalised investment recommendations, or a solicitation to buy, sell, or hold any financial instrument. You are solely responsible for your own financial decisions.
                  </p>
                </div>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">2. Client-Operated Tools</h3>
                  <p>
                    The Our Wealth | Era JPBot and any associated software provided as part of your enrolment are proprietary execution assistance tools. These tools are designed to support your own order placement and trade management decisions. At no point does Our Wealth | Era operate, manage, or execute trades on your behalf. Full control and responsibility for usage remains with the client at all times.
                  </p>
                </div>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">3. No Guarantee of Results</h3>
                  <p>
                    Individual outcomes will vary based on a wide range of personal, market, and behavioural factors. Our Wealth | Era makes no representation, warranty, or promise — express or implied — regarding the financial results of applying any strategy, tool, or methodology taught within the program. Any performance examples used in our materials are illustrative only and are not a guarantee of future results.
                  </p>
                </div>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">4. Intellectual Property</h3>
                  <p>
                    All course content, proprietary trading frameworks, software, branding, and associated materials are the exclusive intellectual property of Our Wealth | Era. Unauthorised reproduction, redistribution, resale, or sharing of any program content — in part or in full — is strictly prohibited and may result in immediate termination of access and legal action where applicable.
                  </p>
                </div>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">5. Refund Policy</h3>
                  <p>
                    Our Wealth | Era stands behind the integrity and quality of its methodology. A full 30-day satisfaction guarantee is available to all new students, subject to the terms and conditions confirmed at the point of enrolment. Refund requests must be formally submitted within 30 calendar days of the original purchase date.
                  </p>
                </div>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">6. Third-Party Platforms</h3>
                  <p>
                    Our Wealth | Era is an independent education provider. Any references to CMC Markets, brokerage platforms, or other third-party services within our program are provided solely for context and educational purposes. Our Wealth | Era has no affiliation with, is not endorsed by, and holds no commercial relationship with CMC Markets or any other third-party platform referenced in our materials.
                  </p>
                </div>

                <div className="space-y-1">
                  <h3 className="text-amber-400 font-bold uppercase tracking-wider text-xs">7. Acceptance of Risk</h3>
                  <p>
                    By participating in any Our Wealth | Era program, you confirm that you understand the inherent risks associated with futures and forex trading, that you are trading with capital you can afford to lose, and that you bear full and sole responsibility for all trading decisions made during and after the program.
                  </p>
                </div>

                <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-4 text-xs text-slate-500">
                  These Terms of Service were last updated in 2026 and are subject to change. Continued use of Our Wealth | Era services following any update constitutes acceptance of the revised terms. For inquiries, contact <span className="text-amber-500/70">info@ourwealthera.com</span>.
                </div>
              </div>

              <div className="px-8 pb-6">
                <button
                  onClick={() => setIsTosOpen(false)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-3 rounded-lg transition-colors"
                >
                  I Understand
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- PAGE COMPONENTS ---

const ApplicationPage = () => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }} 
      animate={{ opacity: 1, y: 0 }} 
      exit={{ opacity: 0, y: -10 }}
      className="max-w-3xl mx-auto px-4 py-16"
    >
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-white mb-4">Mentorship Application</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          We operate a closed community to ensure the quality of our mentorship. Please provide details below to initiate a dialogue with our team.
        </p>
      </div>
      <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6 mb-8 space-y-6">
        <div className="flex items-start">
          <Mail className="w-5 h-5 text-amber-500 mt-1 mr-4 shrink-0" />
          <div>
            <h3 className="text-white font-semibold text-sm">Email Inquiries</h3>
            <p className="text-slate-400 text-sm">info@ourwealthera.com</p>
          </div>
        </div>
        <div className="flex items-start">
          <MapPin className="w-5 h-5 text-amber-500 mt-1 mr-4 shrink-0" />
          <div>
            <h3 className="text-white font-semibold text-sm">Global Headquarters</h3>
            <p className="text-slate-400 text-sm">Hong Kong, SAR</p>
          </div>
        </div>
        <div className="flex items-start">
          <Building2 className="w-5 h-5 text-amber-500 mt-1 mr-4 shrink-0" />
          <div>
            <h3 className="text-white font-semibold text-sm">Institutional</h3>
            <p className="text-slate-400 text-sm">For fund licensing, please contact our legal department directly.</p>
          </div>
        </div>
      </div>
      <div className="bg-slate-900/30 border border-slate-800 rounded-lg p-8">
        <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">First Name</label>
              <input type="text" placeholder="John" className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Last Name</label>
              <input type="text" placeholder="Doe" className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Email Address</label>
            <input type="email" placeholder="john@example.com" className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Current Capital</label>
            <div className="relative">
              <select defaultValue="" className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-white appearance-none focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors cursor-pointer">
                <option value="" disabled>Select Range...</option>
                <option value="10-50">$10,000 - $50,000</option>
                <option value="50-250">$50,000 - $250,000</option>
                <option value="250-1m">$250,000 - $1,000,000</option>
                <option value="1m+">$1,000,000+</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Experience Level</label>
            <div className="relative">
              <select defaultValue="" className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-white appearance-none focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors cursor-pointer">
                <option value="" disabled>Select Level...</option>
                <option value="beginner">Beginner (0-2 years)</option>
                <option value="intermediate">Intermediate (2-5 years)</option>
                <option value="advanced">Advanced (5+ years)</option>
                <option value="professional">Professional / Institutional</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Your Goals</label>
            <textarea rows="4" placeholder="Briefly describe your financial objectives..." className="w-full bg-slate-950 border border-slate-800 rounded-md px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors resize-none"></textarea>
          </div>
          <button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-4 rounded-lg transition-all transform hover:scale-[1.01] shadow-lg shadow-amber-500/20 flex items-center justify-center">
            Submit Application <Send className="ml-2 w-4 h-4" />
          </button>
          <p className="text-center text-slate-600 text-xs mt-4">Your information is kept strictly confidential. We do not sell data.</p>
        </form>
      </div>
    </motion.div>
  );
};

const CoursePage = ({ onCtaClick }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-900/20 via-slate-950 to-slate-950" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">Your Wealth, <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-amber-500">On Autopilot.</span></h1>
          <p className="text-lg md:text-xl text-slate-400 mb-8 leading-relaxed max-w-2xl mx-auto">There is a quiet confidence that comes from knowing your financial future is being built in the background. It's the feeling of waking up to the realization that your assets have been productive while you slept.</p>
          <p className="text-xl md:text-2xl text-white font-semibold mb-10 max-w-3xl mx-auto border-l-4 border-amber-500 pl-6 py-2 bg-slate-900/50 rounded-r-lg">This is the difference between working for money and having money work for you.</p>
          <button onClick={onCtaClick} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-4 px-8 rounded-lg transition-all transform hover:scale-105 shadow-lg shadow-amber-500/25 flex items-center mx-auto">
            Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

      <section className="py-16 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">The "Digital Landlord" Mindset</h2>
            <p className="text-slate-400 mb-4 leading-relaxed">For decades, we've been told that Real Estate is the only true path to passive income. We are taught to buy physical properties, deal with tenants, and wait years for appreciation. But in the digital age, wealth moves faster.</p>
            <p className="text-slate-300 font-medium leading-relaxed border-l-4 border-amber-500 pl-4">The "Our Wealth | Era" Method takes the stability of that "landlord mindset" and applies it to the speed of the global markets.</p>
          </div>
          <div className="bg-slate-800/50 p-8 rounded-2xl border border-slate-700">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center"><Clock className="text-amber-500 mr-2" /> The 15-Minute Routine</h3>
            <p className="text-slate-400 text-sm mb-6">Imagine a system that doesn't require you to predict the future or stare at a monitor for eight hours a day. Instead of chasing the market, you simply set your "Blocks"—mathematical zones of accumulation.</p>
            <ul className="space-y-3">
              {['No staring at charts', 'No emotional trading', 'No predicting direction'].map((item, i) => (
                <li key={i} className="flex items-center text-slate-300 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 mr-2" /> {item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 max-w-4xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why We Stopped Guessing</h2>
          <p className="text-slate-400">The financial industry sells a lie: that to be successful, you must be smarter than the market.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-8 rounded-xl border border-slate-800 hover:border-amber-500/30 transition-all">
            <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mb-6"><BrainCircuit className="text-red-400 w-6 h-6" /></div>
            <h3 className="text-xl font-bold text-white mb-3">The Gambler's Game</h3>
            <p className="text-slate-400 text-sm leading-relaxed">Most traders try to time the exact bottom. They panic when the market moves against them. Relying on "gut feeling" is like trying to build a skyscraper without a blueprint. That is a gambler's game. And the house always wins.</p>
          </div>
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-8 rounded-xl border border-slate-800 hover:border-amber-500/30 transition-all">
            <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mb-6"><TrendingUp className="text-emerald-400 w-6 h-6" /></div>
            <h3 className="text-xl font-bold text-white mb-3">Cost Basis Engineering</h3>
            <p className="text-slate-400 text-sm leading-relaxed">We don't fight the market; we structure it. While others panic-sell during a drop, our students execute "Zone Acquisition." We view a price drop as a wholesaler views a discount—an opportunity to acquire inventory.</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">The 5 Pillars of Your New Routine</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'The "Wholesaler" Mindset', desc: 'Stop chasing green candles. Learn the institutional art of "Inventory Accumulation." Learn to love the "red days" because that is when your business acquires its best assets.' },
              { title: 'The Silent Partner', desc: 'Access our proprietary execution tool. It calculates your zones, places your orders, and sets your Take Profit levels. It ensures your business runs while you are on the back nine.' },
              { title: 'Mathematical Stoicism', desc: 'Fear comes from the unknown. When you understand the math behind the Block, you develop "Zone Confidence." You won\'t feel adrenaline; you will see the math playing out.' },
              { title: 'The "Capital Fortress"', desc: 'We teach a specific "Capital Deployment" protocol that ensures you are never over-leveraged. Structure your account to withstand volatility that wipes out the amateurs.' },
              { title: 'The Morning Briefing', desc: 'Join a community of "Asset Managers" for a daily briefing on Nasdaq and Gold. We analyze the structure, set the tone, and then disconnect to live our lives.' }
            ].map((pillar, index) => (
              <div key={index} className="bg-slate-950 p-6 rounded-lg border border-slate-800 relative group hover:-translate-y-1 transition-transform duration-300">
                <div className="absolute top-0 right-0 p-4 opacity-10 font-bold text-4xl text-amber-500">{index + 1}</div>
                <h3 className="text-lg font-bold text-amber-500 mb-3">{pillar.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 text-center px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Your Future, Structured.</h2>
          <p className="text-slate-400 mb-8 text-lg">By the end of this mentorship, you will have a Business Plan for the futures market. Master the Block. Ignore the Noise. Live the Life.</p>
          <button onClick={onCtaClick} className="bg-white text-slate-950 hover:bg-slate-200 font-bold py-4 px-10 rounded-full transition-colors shadow-xl">Speak with a Strategy Coach Today</button>
        </div>
      </section>
    </motion.div>
  );
};

const AboutPage = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-5xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <div className="flex justify-center mb-8">
          <BrandLogo className="w-80 h-80 md:w-96 md:h-96 shadow-2xl shadow-amber-500/20" />
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">About The "Our Wealth | Era" Method</h1>
        
        {/* Slogan integrated into About Page header */}
        <Slogan className="mb-8" />
        
        <div className="h-1 w-20 bg-amber-500 mx-auto rounded-full"></div>
      </div>
      <div className="space-y-12">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 md:w-1/3">
            <Users className="w-10 h-10 text-amber-500 mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Our Mission</h3>
            <p className="text-slate-400 text-sm">To decouple income from time. We believe that true wealth is not just a number in a bank account, but the autonomy to live life on your own terms.</p>
          </div>
          <div className="md:w-2/3">
            <h2 className="text-2xl font-bold text-white mb-4">We Are Not "Traders"</h2>
            <p className="text-slate-400 leading-relaxed mb-4">The word "trader" implies a frantic, high-stress lifestyle. It conjures images of people shouting at screens, glued to news feeds, and living in a constant state of fight-or-flight.</p>
            <p className="text-slate-400 leading-relaxed">We are <strong>Asset Managers</strong>. We are <strong>System Architects</strong>. The "Our Wealth | Era" Method was born out of a necessity to escape the "hustle culture" of Wall Street. We realized that the most profitable systems are the ones that remove human emotion from the equation.</p>
          </div>
        </div>

        {/* Updated Founder Section with Image */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden flex flex-col md:flex-row shadow-xl">
          
          {/* Founder Image Container */}
          <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-slate-800">
            <img 
              src="aragonai2.jpeg" 
              alt="Francis So - Founder of Our Wealth Era" 
              className="absolute inset-0 w-full h-full object-cover object-top md:object-center"
            />
            {/* Subtle gradient overlay to blend edge on mobile, though hard edge looks clean too */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent md:hidden"></div>
          </div>

          {/* Founder Text Container */}
          <div className="p-8 md:p-10 lg:p-12 md:w-3/5 flex flex-col justify-center">
            <h3 className="text-amber-500 font-bold tracking-widest uppercase text-sm mb-2">The Founder</h3>
            <h2 className="text-3xl font-bold text-white mb-1">Francis So</h2>
            <p className="text-amber-500/80 font-medium text-sm mb-8">CFA Charterholder · Senior Banking Executive · Quantitative Strategist</p>
            
            <div className="prose prose-invert max-w-none text-slate-400 text-sm leading-relaxed">
              <p className="mb-4">
                Francis So spent over two decades as a senior executive at leading international financial institutions. Managing multi-billion dollar balance sheets across Asia, he built his career on disciplined, process-driven capital management — the kind that compounds quietly and holds up under pressure.
              </p>
              <p className="mb-4">
                In 2021, he transitioned into digital asset markets. Immersed in the mechanics of decentralised exchanges and on-chain automated trading, Francis encountered something that institutional finance had long understood but rarely made accessible to ordinary investors: that systematic, rules-based execution consistently outperforms human discretion. Emotion is the enemy of returns. Automation is the answer.
              </p>
              
              <blockquote className="border-l-4 border-amber-500 pl-6 py-3 my-6 bg-slate-950/50 rounded-r-lg italic text-slate-300 font-medium text-base">
                "The best traders I ever met weren't the smartest people in the room. They were the most disciplined. I wanted to build a system that made discipline automatic."
              </blockquote>
              
              <p className="mb-4">
                Together with a close team of partners in technology and finance, Francis spent the past nine months quietly building and stress-testing that system — running it live, with real capital, for a small group of clients. The results have been consistent. Now, for the first time, Our Wealth | Era is opening access beyond that founding circle.
              </p>
              <p>
                Our Wealth | Era is the convergence of everything the founding team has built across their careers: the risk discipline of seasoned bankers, the technical insight of digital asset operators, and a fully automated trading engine that works while you don't have to. Francis leads that team — and this is their shared life's work.
              </p>
            </div>
          </div>

        </div>
      </div>
    </motion.div>
  );
};

const PurchasePage = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="max-w-6xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Invest In Your Freedom</h1>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto">Stop trading your time for money. Acquire the proprietary skill set that turns volatility into an asset.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div className="space-y-6">
          <div className="bg-slate-900 p-6 rounded-xl border border-slate-800">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center"><Shield className="mr-2 text-amber-500" /> What You Receive</h3>
            <ul className="space-y-4">
              {['Full Access to The "Our Wealth | Era" Masterclass','Proprietary "Our Wealth | Era" JPBot Software License','Daily "Our Wealth | Era" Market Analysis','The Capital Fortress Risk Management Protocol','Lifetime Community Access','Direct Mentorship Support'].map((item, i) => (
                <li key={i} className="flex items-start text-slate-300"><CheckCircle2 className="w-5 h-5 text-emerald-500 mr-3 shrink-0 mt-0.5" /><span>{item}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-900/50 p-6 rounded-xl border border-slate-800/50">
            <h4 className="text-white font-bold mb-2">100% Satisfaction Guarantee</h4>
            <p className="text-slate-400 text-sm">We are so confident in the mathematical validity of this system that we offer a full 30-day money-back guarantee if you feel the logic does not hold up to scrutiny.</p>
          </div>
        </div>
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl border border-amber-500/30 p-8 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 bg-amber-500 text-slate-900 text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">Limited Enrollment</div>
          <h2 className="text-2xl font-bold text-white mb-6 text-center">The Mentorship Program</h2>
          
          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-8 bg-slate-950/50 py-3 rounded-xl border border-slate-800">
            <span className={`text-sm ${!isAnnual ? 'text-white font-bold' : 'text-slate-400'}`}>Monthly</span>
            <button 
              onClick={() => setIsAnnual(!isAnnual)} 
              className="relative inline-flex h-7 w-14 items-center rounded-full bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-amber-500 transition-transform duration-200 ease-in-out ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm flex items-center ${isAnnual ? 'text-white font-bold' : 'text-slate-400'}`}>
              Annually 
              {/* Conditionally render the discount badge and update to 30% */}
              {isAnnual && (
                <span className="ml-2 text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                  Save 16.5%
                </span>
              )}
            </span>
          </div>

          <div className="flex items-end justify-center mb-8">
            <span className="text-5xl font-bold text-white">${isAnnual ? '3,699 USD' : '369 USD'}</span>
            <span className="text-slate-400 ml-2 mb-2">/ {isAnnual ? 'year' : 'month'}</span>
          </div>
          <button className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-4 rounded-lg transition-all transform hover:scale-[1.02] shadow-lg shadow-amber-500/25 mb-4 flex justify-center items-center">
            <Lock className="w-4 h-4 mr-2" /> Secure Your Spot
          </button>
          <p className="text-center text-slate-500 text-xs">Secure 256-bit SSL Encrypted Payment. <br/>Instant Access upon enrollment.</p>
          <div className="mt-8 pt-8 border-t border-slate-700">
            <p className="text-slate-300 text-sm font-medium mb-4 text-center">Join students who have already:</p>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-slate-950/50 rounded p-2"><div className="text-emerald-400 font-bold">Reclaimed</div><div className="text-slate-500 text-xs">Their Time</div></div>
              <div className="bg-slate-950/50 rounded p-2"><div className="text-emerald-400 font-bold">Automated</div><div className="text-slate-500 text-xs">Their Income</div></div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default OurWealthEraWebsite;